import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Select,
  Space,
  message,
  Spin,
  Tag,
  Tooltip,
  Modal,
  Input,
  Form,
  Collapse,
  Typography,
  Drawer,
  List,
  Divider,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  EyeOutlined,
  CodeOutlined,
  HistoryOutlined,
  RollbackOutlined,
  LeftOutlined,
  FileTextOutlined,
  SettingOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from '@ant-design/icons';
import Editor from '@monaco-editor/react';
import { Card } from '../components/design-system/Card';
import { templatesApi, DocumentTemplate, DocumentTemplateVersion, contractsApi, snippetsApi } from '../services/api';
import dayjs from 'dayjs';

const { Panel } = Collapse;
const { Text } = Typography;

const VARIABLE_GROUPS = [
  {
    key: 'company',
    label: 'Company',
    variables: [
      { name: 'company.name', description: 'Company name (Rimtrade International)' },
      { name: 'company.address', description: 'Full company address' },
      { name: 'company.phone', description: 'Company phone number' },
      { name: 'company.email', description: 'Company email' },
      { name: 'company.website', description: 'Company website' },
      { name: 'logoUrl', description: 'URL to company logo image' },
      { name: 'signatureUrl', description: 'URL to signature image' },
    ],
  },
  {
    key: 'contract',
    label: 'Contract',
    variables: [
      { name: 'contract.reference', description: 'Contract reference (e.g., CR-BN24001)' },
      { name: 'contract.contractDate', description: 'Contract date formatted' },
      { name: 'contract.status', description: 'Contract status' },
      { name: 'contract.commodity', description: 'Main commodity' },
      { name: 'contract.hsCode', description: 'HS Code' },
      { name: 'contract.paymentTermsSeller', description: 'Payment terms for seller' },
      { name: 'contract.paymentTermsBuyer', description: 'Payment terms for buyer' },
      { name: 'contract.basisSeller', description: 'Price basis for seller' },
      { name: 'contract.basisBuyer', description: 'Price basis for buyer' },
      { name: 'contract.commissionPercentage', description: 'Commission percentage' },
      { name: 'contract.sellerNotes', description: 'Notes for seller contract' },
      { name: 'contract.buyerNotes', description: 'Notes for buyer contract' },
      { name: 'contract.documents', description: 'Document requirements' },
      { name: 'contract.revised', description: 'Whether contract is revised' },
      { name: 'contract.shipmentsLabel', description: 'Shipments date label' },
    ],
  },
  {
    key: 'supplier',
    label: 'Supplier',
    variables: [
      { name: 'supplier.name', description: 'Supplier full name' },
      { name: 'supplier.shortName', description: 'Supplier short name/code' },
      { name: 'supplier.address', description: 'Supplier address' },
      { name: 'supplier.country', description: 'Supplier country' },
      { name: 'supplier.code', description: 'Supplier code' },
    ],
  },
  {
    key: 'buyer',
    label: 'Buyer',
    variables: [
      { name: 'buyer.name', description: 'Buyer full name' },
      { name: 'buyer.shortName', description: 'Buyer short name/code' },
      { name: 'buyer.address', description: 'Buyer address' },
      { name: 'buyer.country', description: 'Buyer country' },
      { name: 'buyer.code', description: 'Buyer code' },
    ],
  },
  {
    key: 'seller',
    label: 'Seller (if applicable)',
    variables: [
      { name: 'seller.name', description: 'Seller name (if set)' },
      { name: 'seller.address', description: 'Seller address (if set)' },
    ],
  },
  {
    key: 'shipment',
    label: 'Shipment (use in #each)',
    variables: [
      { name: 'shipments', description: 'Array of shipments - use {{#each shipments}}' },
      { name: 'reference', description: 'Shipment reference (inside each)' },
      { name: 'shipmentNumber', description: 'Shipment number' },
      { name: 'startDate', description: 'Shipment start date' },
      { name: 'endDate', description: 'Shipment end date' },
      { name: 'portOfLoading', description: 'Port of loading' },
      { name: 'portOfDischarge', description: 'Port of discharge' },
      { name: 'bookingNumber', description: 'Booking number' },
      { name: 'etd', description: 'Estimated time of departure' },
      { name: 'eta', description: 'Estimated time of arrival' },
      { name: 'totalLbs', description: 'Total pounds' },
      { name: 'totalKgs', description: 'Total kilograms' },
      { name: 'totalUnits', description: 'Total units' },
    ],
  },
  {
    key: 'container',
    label: 'Container (use in #each)',
    variables: [
      { name: 'containers', description: 'Array of containers - use {{#each containers}}' },
      { name: 'containerNumber', description: 'Container number (inside each)' },
      { name: 'sealNumber', description: 'Seal number' },
      { name: 'equipmentType', description: 'Equipment type (40HC, etc.)' },
      { name: 'netWeight', description: 'Net weight' },
      { name: 'grossWeight', description: 'Gross weight' },
    ],
  },
  {
    key: 'goods',
    label: 'Goods (use in #each)',
    variables: [
      { name: 'goods', description: 'Array of goods - use {{#each goods}}' },
      { name: 'description', description: 'Goods description (inside each)' },
      { name: 'variety', description: 'Variety' },
      { name: 'grade', description: 'Grade' },
      { name: 'size', description: 'Size' },
      { name: 'quantity', description: 'Quantity' },
      { name: 'unit', description: 'Unit of measure' },
      { name: 'priceSeller', description: 'Price for seller' },
      { name: 'priceBuyer', description: 'Price for buyer' },
      { name: 'totalSeller', description: 'Total seller amount' },
      { name: 'totalBuyer', description: 'Total buyer amount' },
    ],
  },
  {
    key: 'summary',
    label: 'Summary & Totals',
    variables: [
      { name: 'hasMultipleShipments', description: 'Boolean: has multiple shipments' },
      { name: 'containerCount', description: 'Total container count' },
      { name: 'equipment', description: 'Equipment summary' },
      { name: 'goodsSummary', description: 'Summary of all goods' },
      { name: 'goodsDetail', description: 'Detailed goods breakdown' },
      { name: 'totals.lbs', description: 'Total pounds' },
      { name: 'totals.kgs', description: 'Total kilograms' },
      { name: 'totals.units', description: 'Total units' },
      { name: 'totals.seller', description: 'Total seller amount' },
      { name: 'totals.buyer', description: 'Total buyer amount' },
      { name: 'today', description: 'Current date formatted' },
      { name: 'generatedAt', description: 'Generation timestamp' },
    ],
  },
  {
    key: 'helpers',
    label: 'Handlebars Helpers',
    variables: [
      { name: 'formatCurrency value', description: '{{formatCurrency amount}} - $1,234.56' },
      { name: 'formatDate value', description: '{{formatDate date}} - Jan 8, 2026' },
      { name: 'formatDateLong value', description: '{{formatDateLong date}} - January 8, 2026' },
      { name: 'formatDateTime value', description: '{{formatDateTime date}} - Jan 8, 2026 4:30 PM' },
      { name: 'formatNumber value', description: '{{formatNumber num}} - 1,234.56' },
      { name: '#if condition', description: '{{#if value}}...{{/if}}' },
      { name: '#each array', description: '{{#each items}}...{{/each}}' },
      { name: 'eq a b', description: '{{#if (eq status "active")}}' },
      { name: 'ne a b', description: '{{#if (ne status "closed")}}' },
      { name: 'gt a b', description: '{{#if (gt amount 100)}}' },
      { name: 'lt a b', description: '{{#if (lt amount 100)}}' },
      { name: 'and a b', description: '{{#if (and a b)}}' },
      { name: 'or a b', description: '{{#if (or a b)}}' },
      { name: 'default value fallback', description: '{{default value "N/A"}}' },
      { name: 'uppercase value', description: '{{uppercase text}}' },
      { name: 'lowercase value', description: '{{lowercase text}}' },
    ],
  },
];

const TYPE_LABELS: Record<string, string> = {
  CONTRACT_SUMMARY: 'Contract Summary',
  BOOKING_REQUEST: 'Booking Request',
  SHIPPING_INSTRUCTION: 'Shipping Instruction',
  COMMERCIAL_INVOICE: 'Commercial Invoice',
  PACKING_LIST: 'Packing List',
  BILL_OF_LADING: 'Bill of Lading',
  BUYER_CONTRACT: 'Buyer Contract',
  SELLER_CONTRACT: 'Seller Contract',
};

interface Contract {
  id: string;
  reference: string;
  supplier?: { name: string };
  buyer?: { name: string };
}

const TemplateEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const editorRef = useRef<any>(null);

  const isNew = !id || id === 'new';
  const showPreviewOnLoad = searchParams.get('preview') === 'true';

  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [variableSidebarOpen, setVariableSidebarOpen] = useState(true);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [showCode, setShowCode] = useState(!showPreviewOnLoad);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsForm] = Form.useForm();

  const { data: template, isLoading: templateLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: () => templatesApi.get(id!),
    enabled: !!id && !isNew,
  });

  const { data: typesData } = useQuery({
    queryKey: ['template-types'],
    queryFn: () => templatesApi.getTypes(),
  });

  const { data: contractsData } = useQuery({
    queryKey: ['contracts-for-preview'],
    queryFn: () => contractsApi.getContracts({ limit: 50 }),
  });

  const { data: snippetsData } = useQuery({
    queryKey: ['active-snippets'],
    queryFn: () => snippetsApi.getActive(),
  });

  const contracts = (contractsData as any)?.contracts || [];
  const typeOptions = typesData?.types || [];
  const activeSnippets = snippetsData?.snippets || [];

  useEffect(() => {
    if (template && !isNew) {
      setContent(template.content);
      setName(template.name);
      setType(template.type);
      setDescription(template.description || '');
      settingsForm.setFieldsValue({
        name: template.name,
        description: template.description,
        isDefault: template.isDefault,
        isActive: template.isActive,
      });
    }
  }, [template, isNew, settingsForm]);

  useEffect(() => {
    if (contracts.length > 0 && !selectedContractId) {
      setSelectedContractId(contracts[0].id);
    }
  }, [contracts, selectedContractId]);

  useEffect(() => {
    if (!template || isNew) return;
    const hasChanges =
      content !== template.content ||
      name !== template.name ||
      description !== (template.description || '');
    setHasUnsavedChanges(hasChanges);
  }, [content, name, description, template, isNew]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const updatePreview = useCallback(async () => {
    if (!content || !selectedContractId) {
      setPreviewHtml('<p style="color: #999; text-align: center; padding: 40px;">Select a contract to preview the template</p>');
      return;
    }

    setPreviewLoading(true);
    try {
      const html = await templatesApi.preview({
        content,
        contractId: selectedContractId,
      });
      setPreviewHtml(html as string);
    } catch (error: any) {
      setPreviewHtml(`<div style="color: red; padding: 20px;">
        <strong>Preview Error:</strong><br/>
        ${error.response?.data?.error || error.message || 'Failed to generate preview'}
      </div>`);
    } finally {
      setPreviewLoading(false);
    }
  }, [content, selectedContractId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!showCode || showPreviewOnLoad) {
        updatePreview();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [content, selectedContractId, showCode, showPreviewOnLoad, updatePreview]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isNew) {
        if (!type) throw new Error('Please select a template type');
        if (!name) throw new Error('Please enter a template name');
        return templatesApi.create({
          type,
          name,
          description: description || undefined,
          content,
          isDefault: false,
          isActive: true,
        });
      } else {
        return templatesApi.update(id!, {
          name,
          description: description || null,
          content,
        });
      }
    },
    onSuccess: (result) => {
      message.success(isNew ? 'Template created' : 'Template saved');
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      if (isNew) {
        navigate(`/settings/templates/${result.id}`, { replace: true });
      } else {
        queryClient.invalidateQueries({ queryKey: ['template', id] });
      }
    },
    onError: (error: any) => {
      message.error(error.message || 'Failed to save template');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: ({ templateId, version }: { templateId: string; version: number }) =>
      templatesApi.restore(templateId, version),
    onSuccess: (result) => {
      message.success(`Restored to version ${result.version}`);
      setContent(result.content);
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['template', id] });
      setVersionHistoryOpen(false);
    },
    onError: () => {
      message.error('Failed to restore version');
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (values: { name: string; description: string; isDefault: boolean; isActive: boolean }) =>
      templatesApi.update(id!, values),
    onSuccess: () => {
      message.success('Settings updated');
      queryClient.invalidateQueries({ queryKey: ['template', id] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setSettingsModalOpen(false);
    },
    onError: () => {
      message.error('Failed to update settings');
    },
  });

  const insertVariable = (variableName: string) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;
    const selection = editor.getSelection();
    const range = selection || editor.getModel()?.getFullModelRange();

    if (!range) return;

    const isHelper = variableName.includes(' ');
    const text = isHelper ? `{{${variableName}}}` : `{{${variableName}}}`;

    editor.executeEdits('insert-variable', [
      {
        range: selection || { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 },
        text,
        forceMoveMarkers: true,
      },
    ]);

    editor.focus();
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to leave?',
        okText: 'Leave',
        cancelText: 'Stay',
        onOk: () => navigate('/settings/templates'),
      });
    } else {
      navigate('/settings/templates');
    }
  };

  const handleEditorMount = (editor: any) => {
    editorRef.current = editor;
  };

  if (templateLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Space>
          <Button icon={<LeftOutlined />} onClick={handleBack}>
            Back
          </Button>
          <div>
            <h1 className="text-h1" style={{ margin: 0 }}>
              {isNew ? 'New Template' : name}
            </h1>
            {!isNew && template && (
              <Space size={8} style={{ marginTop: '4px' }}>
                <Tag color="blue">{TYPE_LABELS[template.type] || template.type}</Tag>
                <Tag>v{template.version}</Tag>
                {template.isDefault && <Tag color="green">Default</Tag>}
                {!template.isActive && <Tag color="red">Inactive</Tag>}
                {hasUnsavedChanges && <Tag color="orange">Unsaved</Tag>}
              </Space>
            )}
          </div>
        </Space>

        <Space>
          {!isNew && (
            <>
              <Button icon={<SettingOutlined />} onClick={() => setSettingsModalOpen(true)}>
                Settings
              </Button>
              <Button icon={<HistoryOutlined />} onClick={() => setVersionHistoryOpen(true)}>
                History
              </Button>
            </>
          )}
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={() => saveMutation.mutate()}
            loading={saveMutation.isPending}
            disabled={!content || (isNew && (!type || !name))}
          >
            {isNew ? 'Create' : 'Save'}
          </Button>
        </Space>
      </div>

      {isNew && (
        <Card style={{ marginBottom: '16px' }}>
          <Space size="large" style={{ width: '100%', flexWrap: 'wrap' }}>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>Type *</Text>
              <Select
                placeholder="Select template type"
                value={type || undefined}
                onChange={setType}
                style={{ width: 200 }}
                options={typeOptions.map(t => ({ value: t.value, label: t.label }))}
              />
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>Name *</Text>
              <Input
                placeholder="Template name"
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ width: 250 }}
              />
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>Description</Text>
              <Input
                placeholder="Optional description"
                value={description}
                onChange={e => setDescription(e.target.value)}
                style={{ width: 300 }}
              />
            </div>
          </Space>
        </Card>
      )}

      <Card style={{ marginBottom: '16px', padding: '8px 16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button
              type={showCode ? 'primary' : 'default'}
              icon={<CodeOutlined />}
              onClick={() => setShowCode(true)}
            >
              Code
            </Button>
            <Button
              type={!showCode ? 'primary' : 'default'}
              icon={<EyeOutlined />}
              onClick={() => {
                setShowCode(false);
                updatePreview();
              }}
            >
              Preview
            </Button>
            <Divider type="vertical" />
            <Button
              icon={variableSidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
              onClick={() => setVariableSidebarOpen(!variableSidebarOpen)}
            >
              {variableSidebarOpen ? 'Hide' : 'Show'} Variables
            </Button>
          </Space>

          <Space>
            <Text type="secondary">Preview Contract:</Text>
            <Select
              placeholder="Select contract"
              value={selectedContractId}
              onChange={setSelectedContractId}
              style={{ width: 280 }}
              showSearch
              optionFilterProp="label"
              options={contracts.map((c: Contract) => ({
                value: c.id,
                label: `${c.reference} - ${c.supplier?.name || 'N/A'} → ${c.buyer?.name || 'N/A'}`,
              }))}
            />
          </Space>
        </div>
      </Card>

      <div style={{ flex: 1, display: 'flex', gap: '12px', minHeight: '400px' }}>
        {showCode && (
          <div style={{ 
            flex: 1, 
            border: '1px solid var(--border-color, #d9d9d9)', 
            borderRadius: '8px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <Editor
              height="100%"
              defaultLanguage="html"
              value={content}
              onChange={(value) => setContent(value || '')}
              onMount={handleEditorMount}
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: 'on',
                wordWrap: 'on',
                automaticLayout: true,
                scrollBeyondLastLine: false,
                tabSize: 2,
                renderWhitespace: 'selection',
              }}
              theme="vs-dark"
            />
          </div>
        )}

        {!showCode && (
          <div style={{ 
            flex: 1, 
            border: '1px solid var(--border-color, #d9d9d9)', 
            borderRadius: '8px', 
            overflow: 'auto',
            background: '#fff', 
            padding: '20px',
          }}>
              {previewLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                  <Spin />
                </div>
              ) : (
                <div
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                  style={{ maxWidth: '800px', margin: '0 auto' }}
                />
              )}
          </div>
        )}

        {variableSidebarOpen && (
          <div style={{ 
            width: '280px',
            flexShrink: 0,
            border: '1px solid var(--border-color, #d9d9d9)', 
            borderRadius: '8px', 
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            background: '#fafafa',
          }}>
            <div style={{ 
              padding: '12px 16px', 
              borderBottom: '1px solid var(--border-color, #d9d9d9)',
              background: '#fff',
              fontWeight: 500,
            }}>
              Variables
              <Text type="secondary" style={{ display: 'block', fontSize: '11px', marginTop: '2px' }}>
                Click to insert at cursor
              </Text>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '8px' }}>
              <Collapse 
                defaultActiveKey={['snippets', 'company', 'contract']} 
                size="small"
                style={{ background: 'transparent', border: 'none' }}
              >
                {activeSnippets.length > 0 && (
                  <Panel 
                    header={<span style={{ fontSize: '12px', color: '#52c41a' }}>Snippets (Partials)</span>} 
                    key="snippets"
                    style={{ marginBottom: '4px', background: '#fff', borderRadius: '4px' }}
                  >
                    {activeSnippets.map(snippet => (
                      <div
                        key={snippet.id}
                        onClick={() => {
                          if (!editorRef.current) return;
                          const editor = editorRef.current;
                          const selection = editor.getSelection();
                          editor.executeEdits('insert-snippet', [
                            {
                              range: selection || { startLineNumber: 1, startColumn: 1, endLineNumber: 1, endColumn: 1 },
                              text: `{{> ${snippet.name}}}`,
                              forceMoveMarkers: true,
                            },
                          ]);
                          editor.focus();
                        }}
                        style={{ 
                          cursor: 'pointer', 
                          padding: '6px 8px',
                          marginBottom: '2px',
                          borderRadius: '4px',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f6ffed'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <code style={{ 
                          color: '#52c41a', 
                          fontSize: '11px',
                          display: 'block',
                        }}>
                          {`{{> ${snippet.name}}}`}
                        </code>
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '1px' }}>
                          {snippet.label}
                        </div>
                      </div>
                    ))}
                  </Panel>
                )}
                {VARIABLE_GROUPS.map(group => (
                  <Panel 
                    header={<span style={{ fontSize: '12px' }}>{group.label}</span>} 
                    key={group.key}
                    style={{ marginBottom: '4px', background: '#fff', borderRadius: '4px' }}
                  >
                    {group.variables.map(item => (
                      <div
                        key={item.name}
                        onClick={() => insertVariable(item.name)}
                        style={{ 
                          cursor: 'pointer', 
                          padding: '6px 8px',
                          marginBottom: '2px',
                          borderRadius: '4px',
                          transition: 'background 0.2s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#e6f4ff'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <code style={{ 
                          color: '#1677ff', 
                          fontSize: '11px',
                          display: 'block',
                        }}>
                          {`{{${item.name}}}`}
                        </code>
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '1px' }}>
                          {item.description}
                        </div>
                      </div>
                    ))}
                  </Panel>
                ))}
              </Collapse>
            </div>
          </div>
        )}
      </div>

      <Drawer
        title="Version History"
        placement="right"
        width={400}
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
      >
        {template?.versions && template.versions.length > 0 ? (
          <List
            dataSource={template.versions}
            renderItem={(version: DocumentTemplateVersion) => (
              <List.Item
                actions={[
                  <Button
                    key="restore"
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() =>
                      restoreMutation.mutate({ templateId: template.id, version: version.version })
                    }
                    loading={restoreMutation.isPending}
                    disabled={version.version === template.version}
                  >
                    {version.version === template.version ? 'Current' : 'Restore'}
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={<FileTextOutlined style={{ fontSize: '24px', color: 'var(--ant-primary-color)' }} />}
                  title={`Version ${version.version}`}
                  description={
                    <div>
                      <div>{dayjs(version.createdAt).format('MMM D, YYYY h:mm A')}</div>
                      {version.createdBy && (
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          by {version.createdBy.firstName} {version.createdBy.lastName}
                        </div>
                      )}
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
            No version history available
          </div>
        )}
      </Drawer>

      <Modal
        title="Template Settings"
        open={settingsModalOpen}
        onCancel={() => setSettingsModalOpen(false)}
        footer={null}
        width={500}
      >
        <Form
          form={settingsForm}
          layout="vertical"
          onFinish={(values) => updateSettingsMutation.mutate(values)}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="isDefault" valuePropName="checked">
            <Space>
              <Input type="checkbox" />
              <span>Set as default template for this type</span>
            </Space>
          </Form.Item>
          <Form.Item name="isActive" valuePropName="checked">
            <Space>
              <Input type="checkbox" />
              <span>Active (available for document generation)</span>
            </Space>
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: '24px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setSettingsModalOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={updateSettingsMutation.isPending}
              >
                Save Settings
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TemplateEditor;
